// useAuthListener Hook - Secure State Reset & Race Condition Fix
// Location: src/hooks/useAuthListener.ts

import { useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { usePlanStore } from '../stores/planStore';
import { useProgressStore } from '../stores/progressStore';
import { useChatStore } from '../stores/chatStore';
import { useShoppingListStore } from '../stores/shoppingListStore';
import type { UserProfile } from '../types';

export const useAuthListener = () => {
  // We don't need to destructure actions here if we access stores directly for resets,
  // but keeping authStore destructuring is fine for the main logic.
  const { login, logout, setLoading } = useAuthStore();

  const mapProfileToUser = (profile: any): UserProfile => {
    return {
      id: profile.id,
      email: profile.email,
      firstName: profile.first_name,
      lastName: profile.last_name,
      plan: profile.plan ?? null,
      role: profile.role || 'user',
      stripeCustomerId: profile.stripe_customer_id,
      onboarding_complete: profile.onboarding_complete || false,
      createdAt: profile.created_at,
      updatedAt: profile.updated_at,
      birthDate: profile.birth_date,
      gender: profile.gender,
      age: profile.age,
      currentWeight: profile.current_weight,
      targetWeight: profile.target_weight,
      height: profile.height,
      goal: profile.goal,
      activityLevel: profile.activity_level,
      weeklyGoal: profile.weekly_goal,
      dietaryPreferences: profile.dietary_preferences || {
        dietType: null,
        allergies: [],
        dislikes: [],
      },
      language: profile.language || 'en',
      is_suspended: profile.is_suspended === true,
      referral_code: profile.referral_code ?? null,
      referred_by_code: profile.referred_by_code ?? null,
      referral_credit_chf: profile.referral_credit_chf ?? 0,
    };
  };

  // --- GLOBAL RESET FUNCTION ---
  // This ensures absolutely no data from the previous user remains in memory or local storage.
  const handleGlobalLogout = () => {
    // console.log('Executing Global State Wipe...');

    // 1. Clear Auth
    logout();

    // 2. Clear Plan Store (Has persist, needs explicit clear)
    usePlanStore.getState().clearPlan();

    // 3. Clear Progress Store (Has persist, needs explicit reset)
    useProgressStore.getState().reset();

    // 4. Clear Chat Store (Manual State Reset since it lacks a full reset action)
    useChatStore.getState().resetStore();

    // 5. Clear Shopping List (No persist, but state remains in memory)
    useShoppingListStore.setState({
      items: [],
      isLoading: false,
      isAdding: false
    });

    // 6. Clear Local Storage (Nuclear option for persisted stores if needed)
    // Optional: mostly handled by store resets, but good for safety.
    localStorage.removeItem('nutribot-plan-storage');
    localStorage.removeItem('progress-storage');

    setLoading(false);
  };

  useEffect(() => {
    let mounted = true; // Race Condition Guard
    setLoading(true);

    const resolveSession = async (session: any) => {
      if (!session?.user) {
        if (mounted) handleGlobalLogout();
        return;
      }

      // console.log('Resolving session for:', session.user.id);

      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        // GUARD: If component unmounted during await, stop here.
        if (!mounted) return;

        if (error) {
          // console.error('Profile fetch failed:', error);

          // If profile doesn't exist, sign out
          if (error.code === 'PGRST116') {
            // console.error('Profile not found - signing out');
            handleGlobalLogout();
            return;
          }

          // For other errors, just stop loading (don't logout)
          setLoading(false);
          return;
        }

        if (!profile) {
          // console.error('Profile is null');
          handleGlobalLogout();
          return;
        }

        // Valid profile - update store
        login(mapProfileToUser(profile));
        setLoading(false);

      } catch (err) {
        // console.error('Unexpected error in resolveSession:', err);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    const { data: { subscription } } =
      supabase.auth.onAuthStateChange((event, session) => {
        // console.log('Auth event:', event);

        // GUARD: If unmounted, ignore events (rare but possible)
        if (!mounted) return;

        switch (event) {
          case 'INITIAL_SESSION':
          case 'SIGNED_IN':
          case 'USER_UPDATED':
            resolveSession(session);
            break;

          case 'SIGNED_OUT':
            handleGlobalLogout();
            break;

          case 'TOKEN_REFRESHED':
            break;
        }
      });

    return () => {
      mounted = false; // Cleanup: Disable all future state updates from this effect
      subscription.unsubscribe();
    };
  }, [login, logout, setLoading]);
};